import { apiRequests } from '../utilities/axios-factory'
import store from '../store'

let CACHED_TEMPLATES = null
let CACHED_AGENT_CONVERSION_RATE = null
let CACHED_MONEY_ACCOUNT_TYPES = null

const PAYOUT_SETTINGS_FIELD_LABELS = {
  smarttransytFee: 'SmartTransyt Fee',
  smarttransytFeePercentage: 'SmartTransyt Fee Percentage',
  beaconFee: 'Beacon Fee',
  beaconMaintenanceFee: 'Beacon Maintenance Fee',
  perDayDriverFee: 'Per Day Driver Fee',
  perKwHFee: 'Per kWh Fee'
}

const PAYOUT_SETTINGS_FIELD_TOOLTIPS = {
  smarttransytFee: 'Fixed fee to charge the account owner every payout session.',
  smarttransytFeePercentage: 'Percentage of revenue to charge for a payout session.',
  beaconFee: 'Cost charged to a passenger for a replacement beacon if they already had one registered on or after 1 August 2026.',
  beaconMaintenanceFee: 'Fee charged to every ticket purchased in the system.',
  perDayDriverFee: 'Driver fee per day of operation.',
  perKwHFee: 'Fee per kWh used to operate the vehicle for one day.'
}

function getPayoutSettingsFieldLabel (fieldName) {
  if (PAYOUT_SETTINGS_FIELD_LABELS[fieldName]) {
    return PAYOUT_SETTINGS_FIELD_LABELS[fieldName]
  }
  return fieldName
}

function getHoursOfOperationTemplates () {
  return apiRequests.get('/v1/st-setting/hours-of-operation/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    if (result && result.data) {
      result.data = result.data.map((o) => ({ ...o, ...o.data }))
    }

    CACHED_TEMPLATES = { timestamp: Date.now(), data: result.data } || null
    return result.data
  })
}

function getHoursOfOperationTemplate (id) {
  return apiRequests.get('/v1/st-setting/hours-of-operation/' + id + '/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    if (result && result.data) {
      result.data = { ...result.data, ...result.data.data }
    }

    return result.data
  })
}

function getCachedHoursOfOperationTemplates (cacheTimeoutMsecs = 15 * 60000) {
  if (!CACHED_TEMPLATES || CACHED_TEMPLATES.timestamp < (Date.now() + cacheTimeoutMsecs)) {
    return getHoursOfOperationTemplates()
  }

  return CACHED_TEMPLATES.data
}

function addHoursOfOperationTemplate ({ templateName, data }) {
  return apiRequests.post('/v1/st-setting/hours-of-operation/', { data: { templateName, data } }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function updateHoursOfOperationTemplate ({ id, templateName, data }) {
  return apiRequests.patch('/v1/st-setting/' + id + '/hours-of-operation/', { data: { templateName, data } }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function getAgentConversionRatePercent ({ countryId }) {
  return apiRequests.get('/v1/st-setting/agent-conversion-rate/' + countryId + '/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    CACHED_AGENT_CONVERSION_RATE = { timestamp: Date.now(), data: result && result.data }
    return result && result.data
  })
}

function getCachedAgentConversionRatePercent ({ countryId, cacheTimeoutMsecs = 15 * 60000 }) {
  if (!CACHED_AGENT_CONVERSION_RATE || CACHED_AGENT_CONVERSION_RATE.timestamp < (Date.now() + cacheTimeoutMsecs)) {
    return getAgentConversionRatePercent({ countryId })
  }

  return CACHED_AGENT_CONVERSION_RATE.data
}

function deleteStSetting (id) {
  return apiRequests.delete('/v1/st-setting/' + id + '/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function getOfflineNotificationSettings () {
  return apiRequests.get('/v1/st-setting/offline-notifications/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function getOfflineNotificationSetting (id) {
  return apiRequests.get('/v1/st-setting/offline-notification/' + id + '/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function getOfflineNotificationSettingByAgencyId (agencyId, monitorType) {
  return apiRequests.get('/v1/st-setting/offline-notification/agency/' + agencyId + '/', {
    params: {
      monitorType
    },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function addOfflineNotificationSetting ({ agencyId, monitorType, contacts, offlineThresholdMinutes, notificationIntervalMinutes, enabled, consecutiveMapApiFailures, terminalCoordinates }) {
  return apiRequests.post('/v1/st-setting/offline-notification/', {
    agencyId,
    monitorType,
    contacts,
    offlineThresholdMinutes,
    notificationIntervalMinutes,
    enabled,
    consecutiveMapApiFailures,
    terminalCoordinates
  }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function updateOfflineNotificationSetting ({ id, monitorType, contacts, offlineThresholdMinutes, notificationIntervalMinutes, enabled, consecutiveMapApiFailures, terminalCoordinates }) {
  return apiRequests.patch('/v1/st-setting/offline-notification/' + id + '/', {
    monitorType,
    contacts,
    offlineThresholdMinutes,
    notificationIntervalMinutes,
    enabled,
    consecutiveMapApiFailures,
    terminalCoordinates
  }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function deleteOfflineNotificationSetting (id) {
  return apiRequests.delete('/v1/st-setting/offline-notification/' + id + '/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function normalizePayoutSettingsResponse (result) {
  const data = result && result.data ? result.data : null
  if (!data) {
    return data
  }

  return {
    ...data,
    ...(data.data || {})
  }
}

function getPayoutSettings ({ agencyId = 'STRANSITGHA' } = {}) {
  return apiRequests.get('/v1/st-setting/payout-settings/', {
    params: { agencyId },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return normalizePayoutSettingsResponse(result)
  })
}

function updatePayoutSettings ({ agencyId = 'STRANSITGHA', smarttransytFee, smarttransytFeePercentage, beaconFee, beaconMaintenanceFee, perDayDriverFee, perKwHFee }) {
  return apiRequests.patch('/v1/st-setting/payout-settings/', {
    agencyId,
    smarttransytFee,
    smarttransytFeePercentage,
    beaconFee,
    beaconMaintenanceFee,
    perDayDriverFee,
    perKwHFee
  }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return normalizePayoutSettingsResponse(result)
  })
}

function getMoneyAccountTypes () {
  return apiRequests.get('/v1/money-account-type/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    CACHED_MONEY_ACCOUNT_TYPES = result.data ? { timestamp: Date.now(), data: result.data } : null
    return result.data
  })
}

function getItcCallbackUrls () {
  return apiRequests.get('/v1/st-setting/itc-callback-urls/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result && result.data
  })
}

function provisionItcCallbackUrl ({ productFlow, callbackUrl }) {
  return apiRequests.post('/v1/st-setting/itc-callback-urls/provision/', {
    productFlow,
    callbackUrl
  }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result && result.data
  })
}

async function getCachedMoneyAccountTypes (cacheTimeoutMsecs = 15 * 60000, bustCache = false) {
  if (bustCache || !CACHED_MONEY_ACCOUNT_TYPES || CACHED_MONEY_ACCOUNT_TYPES.timestamp + cacheTimeoutMsecs < Date.now()) {
    return getMoneyAccountTypes()
  }

  return CACHED_MONEY_ACCOUNT_TYPES.data
}

function normalizeBeaconSettingsResponse (result) {
  const data = result && result.data ? result.data : null
  if (!data) {
    return data
  }

  return {
    ...data,
    ...(data.data || {})
  }
}

function getBeaconSettings () {
  return apiRequests.get('/v1/st-setting/beacon-settings/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return normalizeBeaconSettingsResponse(result)
  })
}

function updateBeaconSettings ({ portalRegistrationTicketFrequency }) {
  return apiRequests.patch('/v1/st-setting/beacon-settings/', {
    portalRegistrationTicketFrequency
  }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return normalizeBeaconSettingsResponse(result)
  })
}

export {
  PAYOUT_SETTINGS_FIELD_LABELS,
  PAYOUT_SETTINGS_FIELD_TOOLTIPS,
  getPayoutSettingsFieldLabel,
  getCachedMoneyAccountTypes,
  getCachedHoursOfOperationTemplates,
  getHoursOfOperationTemplates,
  getHoursOfOperationTemplate,
  getAgentConversionRatePercent,
  getCachedAgentConversionRatePercent,
  addHoursOfOperationTemplate,
  updateHoursOfOperationTemplate,
  deleteStSetting,
  getOfflineNotificationSettings,
  getOfflineNotificationSetting,
  getOfflineNotificationSettingByAgencyId,
  addOfflineNotificationSetting,
  updateOfflineNotificationSetting,
  deleteOfflineNotificationSetting,
  getPayoutSettings,
  updatePayoutSettings,
  getBeaconSettings,
  updateBeaconSettings,
  getItcCallbackUrls,
  provisionItcCallbackUrl
}
